/* ======================================
   Cart Drawer — Small Batch Serums
   CartAPI helper + <cart-drawer> web component
   ====================================== */

// ------------------------------------
// Cart API Helper
// ------------------------------------
const SECTIONS_TO_RENDER = 'cart-drawer,cart-icon-bubble';

const CartAPI = {
  async add(formData) {
    if (formData instanceof FormData) {
      formData.append('sections', SECTIONS_TO_RENDER);
    }

    const response = await fetch(
      `${window.Shopify.routes.root}cart/add.js`,
      { method: 'POST', body: formData }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || `Add failed: ${response.status}`);
    }
    return response.json();
  },

  async change(key, quantity) {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/change.js`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: String(key),
          quantity,
          sections: SECTIONS_TO_RENDER
        })
      }
    );

    if (!response.ok) throw new Error(`Change failed: ${response.status}`);
    return response.json();
  },

  async update(updates) {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/update.js`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...updates, sections: SECTIONS_TO_RENDER })
      }
    );

    if (!response.ok) throw new Error(`Update failed: ${response.status}`);
    return response.json();
  },

  async clear() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/clear.js`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections: SECTIONS_TO_RENDER })
      }
    );

    if (!response.ok) throw new Error(`Clear failed: ${response.status}`);
    return response.json();
  },

  async get() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!response.ok) throw new Error(`Get failed: ${response.status}`);
    return response.json();
  }
};

window.CartAPI = CartAPI;

// ------------------------------------
// <cart-drawer> Web Component
// ------------------------------------
class CartDrawerElement extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.cart-drawer-overlay');
    this.panel = this.querySelector('.cart-drawer');
    this.body = this.querySelector('.cart-drawer__body');
    this.closeBtn = this.querySelector('[data-action="close"]');
    this.countEl = this.querySelector('.cart-drawer__count');
    this.statusEl = this.querySelector('[data-cart-status]');

    this.debounceTimers = {};
    this.pendingRequests = new Map();
    this.triggerElement = null;

    // Event delegation — click
    this.addEventListener('click', this.handleClick.bind(this));

    // Event delegation — quantity input change
    this.addEventListener('change', this.handleChange.bind(this));

    // Close on Escape
    this.handleKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.handleKeydown);

    // Listen for any product form submission globally
    this.handleFormSubmit = this.onFormSubmit.bind(this);
    document.addEventListener('submit', this.handleFormSubmit);

    // Header cart icon triggers — open drawer instead of navigating
    // Use capture phase so we intercept before the <a href="/cart"> navigates
    this.handleCartTrigger = this.onCartTrigger.bind(this);
    document.addEventListener('click', this.handleCartTrigger, true);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('submit', this.handleFormSubmit);
    document.removeEventListener('click', this.handleCartTrigger, true);
  }

  // ------------------------------------
  // Event Handlers
  // ------------------------------------
  handleClick(e) {
    const target = e.target.closest('[data-action]');

    if (e.target.closest('.cart-drawer-overlay')) {
      this.close();
      return;
    }

    if (!target) return;

    const action = target.dataset.action;
    const key = target.dataset.key;

    switch (action) {
      case 'close':
        e.preventDefault();
        this.close();
        break;

      case 'decrease': {
        e.preventDefault();
        const qty = Math.max(0, parseInt(target.dataset.qty, 10) - 1);
        this.updateInputDisplay(key, qty);
        this.debouncedChange(key, qty);
        break;
      }

      case 'increase': {
        e.preventDefault();
        const qty = parseInt(target.dataset.qty, 10) + 1;
        this.updateInputDisplay(key, qty);
        this.debouncedChange(key, qty);
        break;
      }

      case 'remove':
        e.preventDefault();
        this.removeItem(key, target.closest('.cart-item'));
        break;
    }
  }

  handleChange(e) {
    const input = e.target.closest('[data-quantity-input]');
    if (!input) return;

    const key = input.dataset.key;
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    this.debouncedChange(key, qty);
  }

  onKeydown(e) {
    if (e.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  }

  onFormSubmit(e) {
    const form = e.target;
    if (!form || !form.action || !form.action.includes('/cart/add')) return;

    e.preventDefault();

    const submitBtn = e.submitter || form.querySelector('[type="submit"]');
    this.addToCart(form, submitBtn);
  }

  async addToCart(form, submitBtn) {
    if (submitBtn?.hasAttribute('disabled')) return;

    // Find the text element — legacy buttons use <span class="text">, new buttons use direct text
    const textEl = submitBtn?.querySelector('.text, span:not(.icon):not(.badge)') || submitBtn;
    const originalText = textEl?.textContent?.trim();

    if (submitBtn) {
      submitBtn.setAttribute('disabled', '');
    }
    if (textEl) {
      textEl.textContent = 'Adding...';
    }

    try {
      const formData = new FormData(form);
      const data = await CartAPI.add(formData);

      const productTitle =
        form.closest('[data-product-title]')?.dataset.productTitle
        || form.closest('section')?.querySelector('h1, .product-title, .product_name')?.textContent?.trim();

      this.renderSections(data.sections, 'add', productTitle);
      this.open();

      if (textEl) {
        textEl.textContent = 'Added!';
        setTimeout(() => {
          if (submitBtn) submitBtn.removeAttribute('disabled');
          textEl.textContent = originalText || 'Add to Cart';
        }, 1500);
      }
    } catch (err) {
      console.error('Add to cart failed:', err);
      if (submitBtn) submitBtn.removeAttribute('disabled');
      if (textEl) textEl.textContent = originalText || 'Add to Cart';
    }
  }

  onCartTrigger(e) {
    const trigger = e.target.closest('[data-ajax-cart-trigger]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    this.open();
  }

  // ------------------------------------
  // Open / Close
  // ------------------------------------
  open() {
    this.triggerElement = document.activeElement;
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    // Focus close button
    requestAnimationFrame(() => {
      const closeBtn = this.querySelector('[data-action="close"]');
      if (closeBtn) closeBtn.focus();
    });
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    // Return focus to trigger
    if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
      this.triggerElement.focus();
    }
  }

  // ------------------------------------
  // Quantity / Remove
  // ------------------------------------
  updateInputDisplay(key, qty) {
    const input = this.querySelector(`[data-quantity-input][data-key="${key}"]`);
    if (input) input.value = qty;
  }

  debouncedChange(key, qty) {
    clearTimeout(this.debounceTimers[key]);
    this.debounceTimers[key] = setTimeout(() => this.changeQuantity(key, qty), 300);
  }

  async changeQuantity(key, qty) {
    const item = this.querySelector(`.cart-item[data-key="${key}"]`);
    if (item) item.classList.add('is-loading');

    const action = qty === 0 ? 'remove' : 'update';
    const productTitle = item?.querySelector('.cart-item__name')?.textContent?.trim();

    try {
      const data = await CartAPI.change(key, qty);
      this.renderSections(data.sections, action, productTitle);
    } catch (err) {
      console.error('Cart update failed:', err);
      if (item) item.classList.remove('is-loading');
    }
  }

  async removeItem(key, itemEl) {
    if (itemEl) {
      const productTitle = itemEl.querySelector('.cart-item__name')?.textContent?.trim();
      itemEl.classList.add('is-removing');

      // Wait for animation, then perform the change
      itemEl.addEventListener('animationend', async () => {
        try {
          const data = await CartAPI.change(key, 0);
          this.renderSections(data.sections, 'remove', productTitle);
        } catch (err) {
          console.error('Remove failed:', err);
          itemEl.classList.remove('is-removing');
        }
      }, { once: true });
    } else {
      try {
        const data = await CartAPI.change(key, 0);
        this.renderSections(data.sections, 'remove');
      } catch (err) {
        console.error('Remove failed:', err);
      }
    }
  }

  // ------------------------------------
  // Render
  // ------------------------------------
  renderSections(sections, action, productTitle) {
    if (!sections) return;

    // Update drawer content
    if (sections['cart-drawer']) {
      const html = this.getSectionHTML(sections['cart-drawer']);
      if (this.body) this.body.innerHTML = html;

      // Update item count in header
      const inner = this.body.querySelector('#cart-drawer-inner');
      const count = inner?.dataset?.cartItemCount || '0';
      if (this.countEl) this.countEl.textContent = count;
    }

    // Update all header cart count badges
    if (sections['cart-icon-bubble']) {
      const temp = document.createElement('div');
      temp.innerHTML = sections['cart-icon-bubble'];
      const newCount = temp.querySelector('[data-cart-count]')?.textContent || '0';

      // Update new-style [data-cart-count] elements
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = newCount;
      });

      // Update legacy Flex theme [data-bind="itemCount"] elements (header badges)
      document.querySelectorAll('[data-bind="itemCount"]').forEach(el => {
        el.textContent = newCount;
      });

      // Toggle has-cart-count class on header cart icon
      document.querySelectorAll('[data-ajax-cart-trigger]').forEach(el => {
        el.classList.toggle('has-cart-count', parseInt(newCount, 10) > 0);
      });
    }

    // Currency converter re-scan
    this.triggerCurrencyConverter();

    // Screen reader announcement
    this.announce(action, productTitle);

    // GTM events
    this.pushAnalytics(action, productTitle);
  }

  getSectionHTML(sectionHTML) {
    // Shopify wraps section content in <div id="shopify-section-{name}">
    // Extract the inner content
    const temp = document.createElement('div');
    temp.innerHTML = sectionHTML;
    const wrapper = temp.querySelector('[id^="shopify-section-"]');
    return wrapper ? wrapper.innerHTML : sectionHTML;
  }

  // ------------------------------------
  // Third-Party Integrations
  // ------------------------------------
  triggerCurrencyConverter() {
    if (
      typeof window.PXUTheme !== 'undefined' &&
      window.PXUTheme.currencyConverter &&
      typeof window.PXUTheme.currencyConverter.convertCurrencies === 'function'
    ) {
      window.PXUTheme.currencyConverter.convertCurrencies();
    }
  }

  announce(action, productTitle) {
    if (!this.statusEl) {
      this.statusEl = this.querySelector('[data-cart-status]');
    }
    if (!this.statusEl) return;

    const messages = {
      add: productTitle ? `${productTitle} added to cart` : 'Item added to cart',
      remove: productTitle ? `${productTitle} removed from cart` : 'Item removed from cart',
      update: productTitle ? `Quantity updated for ${productTitle}` : 'Cart updated'
    };

    this.statusEl.textContent = messages[action] || 'Cart updated';
  }

  pushAnalytics(action, productTitle) {
    window.dataLayer = window.dataLayer || [];

    if (action === 'add') {
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          items: productTitle ? [{ item_name: productTitle }] : []
        }
      });
    }

    if (action === 'remove') {
      window.dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          items: productTitle ? [{ item_name: productTitle }] : []
        }
      });
    }
  }
}

customElements.define('cart-drawer', CartDrawerElement);

// ------------------------------------
// <product-form> Web Component
// ------------------------------------
class ProductFormElement extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
  }
}

customElements.define('product-form', ProductFormElement);
